// src/components/news/NewsSourceForm.js - News source form (create/edit)
import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { useNotification } from '../../hooks/useNotification';
import { getErrorMessage } from '../../utils/error';

const NewsSourceForm = ({ source, onClose, onSuccess }) => {
  const { showSuccess, showError } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    url: '',
    rss_feed_url: '',
    source_type: 'rss',
    country: '',
    language: 'en'
  });

  useEffect(() => {
    if (source) {
      setFormData({
        name: source.name || '',
        url: source.url || '',
        rss_feed_url: source.rss_feed_url || '',
        source_type: source.source_type || 'rss',
        country: source.country || '',
        language: source.language || 'en'
      });
    }
  }, [source]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (source) {
        // Update existing source
        await api.put(`/api/news/sources/${source.id}`, formData);
        showSuccess('News source updated successfully');
      } else {
        // Create new source
        await api.post('/api/news/sources', formData);
        showSuccess('News source created successfully');
      }

      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (error) {
      console.error('Failed to save news source:', error);
      showError(getErrorMessage(error, 'Failed to save news source'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-shayek-dark-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-shayek-light-300 dark:border-shayek-dark-200">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <h3 className="text-xl font-semibold text-shayek-light-900 dark:text-shayek-dark-900">
            {source ? 'Edit News Source' : 'Add News Source'}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 text-2xl transition-colors"
          >
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              URL *
            </label>
            <input
              type="url"
              value={formData.url}
              onChange={(e) => setFormData({ ...formData, url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              RSS Feed URL
            </label>
            <input
              type="url"
              value={formData.rss_feed_url}
              onChange={(e) => setFormData({ ...formData, rss_feed_url: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
                Source Type
              </label>
              <select
                value={formData.source_type}
                onChange={(e) => setFormData({ ...formData, source_type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              >
                <option value="rss">RSS</option>
                <option value="api">API</option>
                <option value="manual">Manual</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
                Language
              </label>
              <select
                value={formData.language}
                onChange={(e) => setFormData({ ...formData, language: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              >
                <option value="en">English</option>
                <option value="ar">Arabic</option>
                <option value="fr">French</option>
                <option value="es">Spanish</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-shayek-dark-700 mb-2">
              Country
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
              className="w-full px-4 py-2 border border-gray-300 dark:border-shayek-dark-200 rounded-lg bg-white dark:bg-shayek-dark-50 text-shayek-light-900 dark:text-shayek-dark-900 focus:ring-2 focus:ring-shayek-primary focus:border-transparent"
              placeholder="e.g., Jordan, UK, USA"
            />
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm text-white bg-shayek-primary rounded-lg hover:bg-shayek-primary-600 disabled:opacity-50 transition-colors shadow-shayek"
            >
              {loading ? 'Saving...' : source ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsSourceForm;

